import {
	ROUTINE_TEST_NAME_OPTIONS,
	TEST_FREQUENCY_OPTIONS,
} from '#app/routes/mocra+/_form+/components/test-routines/constants.ts'
import { type TestRoutineEntry } from '#app/routes/mocra+/_form+/components/test-routines/schema.ts'

export type RoutineTestNameValue =
	(typeof ROUTINE_TEST_NAME_OPTIONS)[number]['value']
export type RoutineTestFrequencyValue =
	(typeof TEST_FREQUENCY_OPTIONS)[number]['value']

export type TestRoutineDraft = {
	name: RoutineTestNameValue
	nameOther: string
	frequency: RoutineTestFrequencyValue
}

export function createTestRoutineDraft(
	overrides: Partial<TestRoutineDraft> = {},
): TestRoutineDraft {
	return {
		name: ROUTINE_TEST_NAME_OPTIONS[0]?.value ?? 'microbial_release',
		nameOther: '',
		frequency: TEST_FREQUENCY_OPTIONS[0]?.value ?? 'per_lot',
		...overrides,
	}
}

export function sanitizeTestRoutineDraft(
	draft: TestRoutineDraft,
): TestRoutineDraft {
	const name = isRoutineTestNameValue(draft.name) ? draft.name : 'other'
	const frequency = isRoutineTestFrequencyValue(draft.frequency)
		? draft.frequency
		: (TEST_FREQUENCY_OPTIONS[0]?.value ?? 'per_lot')
	const trimmedOther = draft.nameOther.trim()
	return {
		name,
		nameOther: name === 'other' ? trimmedOther : '',
		frequency,
	}
}

export function collectTestRoutineErrors(draft: TestRoutineDraft): string[] {
	const sanitized = sanitizeTestRoutineDraft(draft)
	const errors: string[] = []
	if (sanitized.name === 'other' && sanitized.nameOther.length === 0) {
		errors.push('Enter the test name.')
	}
	return errors
}

export function testRoutineDraftToEntry(
	draft: TestRoutineDraft,
): TestRoutineEntry {
	const sanitized = sanitizeTestRoutineDraft(draft)
	return {
		name: sanitized.name,
		...(sanitized.name === 'other' && sanitized.nameOther
			? { nameOther: sanitized.nameOther }
			: {}),
		frequency: sanitized.frequency,
	}
}

export function testRoutineEntryToDraft(
	entry: TestRoutineEntry,
): TestRoutineDraft {
	const name = isRoutineTestNameValue(entry.name) ? entry.name : 'other'
	const frequency = isRoutineTestFrequencyValue(entry.frequency)
		? entry.frequency
		: (TEST_FREQUENCY_OPTIONS[0]?.value ?? 'per_lot')
	return sanitizeTestRoutineDraft({
		name,
		nameOther:
			name === 'other' && typeof entry.nameOther === 'string'
				? entry.nameOther
				: '',
		frequency,
	})
}

export function testRoutineCardTitle(
	draft: TestRoutineDraft,
	_index: number,
): string {
	const sanitized = sanitizeTestRoutineDraft(draft)
	const displayName = resolveTestRoutineNameLabel(
		sanitized.name,
		sanitized.nameOther,
	)
	return displayName
}

export function formatTestRoutineSummary(draft: TestRoutineDraft): string {
	const sanitized = sanitizeTestRoutineDraft(draft)
	const frequencyLabel = resolveTestRoutineFrequencyLabel(sanitized.frequency)
	return `Frequency: ${frequencyLabel}`
}

export function resolveTestRoutineNameLabel(
	value: RoutineTestNameValue,
	customLabel?: string,
): string {
	if (value === 'other' && customLabel) return customLabel
	return (
		ROUTINE_TEST_NAME_OPTIONS.find((option) => option.value === value)?.label ??
		value
	)
}

export function resolveTestRoutineFrequencyLabel(
	value: RoutineTestFrequencyValue,
): string {
	return (
		TEST_FREQUENCY_OPTIONS.find((option) => option.value === value)?.label ??
		value
	)
}

export function isRoutineTestNameValue(
	value: unknown,
): value is RoutineTestNameValue {
	return ROUTINE_TEST_NAME_OPTIONS.some((option) => option.value === value)
}

export function isRoutineTestFrequencyValue(
	value: unknown,
): value is RoutineTestFrequencyValue {
	return TEST_FREQUENCY_OPTIONS.some((option) => option.value === value)
}
